import { useCallback, useEffect, useRef, useState } from 'react'
import { showToast } from '../../../components/Toast'
import { assetThumb, parseAssetError, planAssets, type ScenePlan, type ScenePlanItem } from '../../../api/assets'
import {
	clearScene,
	clearStickyScene,
	getScene,
	getSceneEpoch,
	getStickyScene,
	isPrimarySceneHost,
	isSceneHostMounted,
	MAX_SCENE_ASSETS,
	mountSceneHost,
	newSceneHost,
	SCENE_REMOVE_EVENT,
	setScene,
	setStickyScene,
	useScene,
	wasSceneChannelsResetRecently,
	type SceneAsset,
	type SceneChannel,
	type SceneRemoveRequest,
} from '../../../api/sceneSelection'
import { fitLayout, pickAssetChannel, SCENE_UNFIT_DEFAULT, SCENE_UNFIT_TEXTS, sceneNoticeLines, sceneRoom } from '../../../utils/characterFit'
import { assetIdsInPrompt, stripAssetTokens } from '../../../prompt-vars/phrases'
import { useGenerate } from '../../../context/GenerateContext'
import { useAuth } from '../../../context/AuthContext'
import { MAX_OMNI_REFS, MAX_OMNI_REFS_WITH_VIDEO } from '../utils/constants'
import type { UploadedFile } from '../utils/uploadHelpers'
import { useSceneTools } from '../../../hooks/useSceneTools'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

/**
 * Применение сцены к виджету (Ф2 «Библиотека сцены»): /assets/plan → фото ассетов в СУЩЕСТВУЮЩИЕ каналы.
 *
 * Раскладку набора считает бэк (резолвер наборов — единственная точка правды: сколько видов чьих, что
 * словами); хук кладёт готовые URL в тот канал, что виджет показывает и шлёт, и запоминает след по
 * каждому ассету — снятие убирает строго его фото. Персонаж-одиночка — та же сцена из одного ассета.
 * Смена модели — пере-план; ассет, чей чип появился в промпте («@»), добавляется сам; юзер убрал фото
 * ассета из слота руками — ассет выходит из сцены вместе со своими чипами.
 *
 * Правила, которые держат сцену целой (проход на ошибки 30.09):
 *  - истина «что в сцене» — стор; пока план в пути — набор, к которому идём (pending). Отдельной памяти о
 *    наборе у хука нет: после внешнего сброса сцены «@Анна» снова добавляет Анну, а не считается уже стоящей;
 *  - фото юзера важнее: в мульти-ряд кладём столько, сколько осталось места (лишнее идёт словами, и строка
 *    статуса это называет), а единственный слот с фото юзера упоминание не трогает — занять его может только
 *    явный выбор в пикере, и тогда вытесненное фото возвращается при снятии ассета;
 *  - у каждого хоста (виджет раздела, страница генератора) свои слоты: сцену, разложенную другим хостом, этот
 *    раскладывает у себя заново — промпт и сама сцена при переходе не меняются;
 *  - копии своих же фото, которые после F5 вернул снимок слотов, убираем по запомненным путям — дублей нет.
 */

const dummyFile = () => null as unknown as File
const path = (u: string) => u.split('?', 1)[0]
const keyOf = (ids: string[]) => [...ids].sort().join(',')
// После неудачного плана тот же набор не запрашиваем заново на каждое нажатие клавиши.
const RETRY_AFTER_MS = 20_000

type Gen = ReturnType<typeof useGenerate>

function channelOf(g: Gen): SceneChannel {
	const model = g.selectedModel
	// Seedance-2 «Кадры»: видим только первый кадр (uploadedImage), а не мульти-ряд референсов.
	return pickAssetChannel({
		supportsReferenceImages: model?.supportsReferenceImages,
		supportsMultipleImages: g.supportsMultipleImages,
		isSeedanceFrames: hasFrameRefModes(model) && (g.settings.seedanceMode ?? 'frames') === 'frames',
	})
}

/** Сколько фото сцены примет канал: ёмкость минус фото самого юзера (own — наши, они место не занимают).
 *  reserved — именованные слоты карточки: место под них держим, даже пока они пусты. */
function roomOf(channel: SceneChannel, g: Gen, own: Set<string>, limits: { photoLimit: number | null; reserved: number }): number {
	const foreign = (list: { url?: string }[]) => list.filter((i) => !i.url || !own.has(path(i.url))).length
	const userPhotos =
		channel === 'uploaded'
			? g.uploadedImage && !(g.uploadedImage.url && own.has(path(g.uploadedImage.url))) ? 1 : 0
			: channel === 'omni' ? foreign(g.omniReferenceImages) : Math.max(foreign(g.referenceImages), limits.reserved)
	return sceneRoom({
		channel,
		userPhotos,
		maxImages: g.selectedModel?.maxImages,
		photoLimit: limits.photoLimit,
		omniLimit: g.referenceVideo ? MAX_OMNI_REFS_WITH_VIDEO : MAX_OMNI_REFS,
	})
}

/** Убрать наши фото из мульти-каналов (фото юзера не трогаем). */
function dropFromRows(g: Gen, own: Set<string>): void {
	if (!own.size) return
	const keep = <T extends { url?: string }>(prev: T[]) => {
		const next = prev.filter((i) => !i.url || !own.has(path(i.url)))
		return next.length === prev.length ? prev : next
	}
	g.setReferenceImages(keep)
	g.setOmniReferenceImages(keep)
}

/** Освободить единственный слот от нашего фото: вернуть вытесненное фото юзера (или пусто). */
function releaseSlot(g: Gen, own: Set<string>, displaced: { current: UploadedFile | null }): void {
	const slot = g.uploadedImage
	if (slot?.url && own.has(path(slot.url))) g.setUploadedImage(displaced.current)
	displaced.current = null
}

interface ApplyOptions {
	prefer?: 'sheet' | 'photos'
	/** Без тоста «уйдут словами»: авто-переприменение и упоминание в тексте — причину показывает строка статуса. */
	quiet?: boolean
	/** Явный выбор в пикере: фото ассета занимает единственный слот, даже если в нём фото юзера. */
	replace?: boolean
}

export function useSceneApply(opts: {
	/** Предел фото карточки (user_photo_limit). */
	photoLimit?: number | null
	/** Сколько именованных фото-слотов у карточки сейчас в игре — место под них сцена не занимает. */
	reservedSlots?: number
} = {}) {
	const gen = useGenerate()
	const { user } = useAuth()
	const userId = user?.id ?? null
	// Последние значения контекста для async-кода (после await замыкание было бы устаревшим).
	const genRef = useRef(gen)
	const limitsRef = useRef({ photoLimit: opts.photoLimit ?? null, reserved: opts.reservedSlots ?? 0 })
	const userRef = useRef<string | null>(userId)
	useEffect(() => {
		genRef.current = gen
		limitsRef.current = { photoLimit: opts.photoLimit ?? null, reserved: opts.reservedSlots ?? 0 }
		userRef.current = userId
	})
	const [host] = useState(newSceneHost)
	useEffect(() => mountSceneHost(host), [host])
	// Библиотека сцены открыта не всем (рубильник в админке): закрыта — упоминания из текста в сцену не встают.
	const tools = useSceneTools()
	// Номер запроса: пришёл ответ старого плана — не применяем (быстрые клики, смена модели).
	const seq = useRef(0)
	// Набор, к которому идём, пока план в пути: «@» и пикер добавляют к нему, а не к ещё не обновлённой сцене.
	const pending = useRef<string[] | null>(null)
	const failed = useRef<{ key: string; at: number } | null>(null)
	// Фото юзера, которое сцена вытеснила из единственного слота (явный выбор в пикере) — вернём при снятии.
	const displaced = useRef<UploadedFile | null>(null)
	const capToastAt = useRef(0)

	/** Что сейчас (или вот-вот) в сцене. */
	const wanted = useCallback((): string[] => pending.current ?? getScene()?.assets.map((a) => a.id) ?? [], [])

	/** Пути фото, которые положила сцена: текущий след и след прошлой сессии вкладки (снимок слотов после F5). */
	const ownPaths = useCallback((): Set<string> => {
		const out = new Set<string>((getScene()?.placedUrls ?? []).map(path))
		for (const p of getStickyScene(userRef.current)?.paths ?? []) out.add(p)
		return out
	}, [])

	const applyScene = useCallback(
		async (list: string[], o: ApplyOptions = {}) => {
			const ids = list.slice(0, MAX_SCENE_ASSETS)
			const my = ++seq.current
			const epoch = getSceneEpoch()
			const prev = getScene()
			if (!ids.length) {
				pending.current = null
				failed.current = null
				const own = ownPaths()
				dropFromRows(genRef.current, own)
				releaseSlot(genRef.current, own, displaced)
				clearScene()
				clearStickyScene()
				return
			}
			const g = genRef.current
			const model = g.selectedModel
			if (!model) return
			pending.current = ids
			const prefer = o.prefer ?? prev?.prefer ?? getStickyScene(userRef.current)?.prefer
			const room = roomOf(channelOf(g), g, ownPaths(), limitsRef.current)
			let plan: ScenePlan
			try {
				plan = await planAssets({
					asset_ids: ids,
					adapter: model.adapter || model.provider,
					model: model.id,
					task: g.isVideo ? 'video' : 'photo',
					prefer,
					// Свободное место в слотах — резолвер раздаст его между ассетами (каждому главный вид, потом вторые).
					...(room > 0 ? { budget: Math.min(room, 30) } : {}),
				})
			} catch (e) {
				// План не получен — слоты НЕ трогаем (никаких полу-раскладок).
				if (my !== seq.current) return
				pending.current = null
				failed.current = { key: keyOf(ids), at: Date.now() }
				if (epoch === getSceneEpoch()) showToast(`Не удалось добавить в сцену: ${parseAssetError(e)}`, 'error')
				return
			}
			if (my !== seq.current) return
			pending.current = null
			// Сцену сбросили снаружи, пока шёл план («Создать видео» из результата, «в работу») — ответ не применяем.
			// Панель успели закрыть (ушли на другую страницу) — тоже: её слотов больше нет, сцену разложит новый хост.
			if (epoch !== getSceneEpoch() || !isSceneHostMounted(host)) return
			failed.current = null
			const g2 = genRef.current
			if (plan.ignored.length) {
				// Чужие или удалённые ассеты: их упоминания в промпт не уходят (сервер тоже их вырежет).
				const stripped = stripAssetTokens(g2.prompt, plan.ignored)
				if (stripped !== g2.prompt) g2.setPrompt(stripped)
			}
			const assets: SceneAsset[] = plan.assets.map((a) => ({
				id: a.id, kind: a.kind, name: a.name, avatarUrl: assetThumb(a), isStock: a.is_stock,
			}))
			const items = plan.fits ? plan.layout.filter((i) => !!i.url) : []
			// Прошлую раскладку (и копии после F5) снимаем ДО новой. Заодно — всё, что уже лежит в слотах по тем же
			// путям, что мы сейчас положим: копия без следа (сцену сбросили, а слоты нет) не должна дать дубль.
			const own = ownPaths()
			for (const i of items) own.add(path(i.url as string))
			dropFromRows(g2, own)
			if (!assets.length) {
				releaseSlot(g2, own, displaced)
				clearScene()
				clearStickyScene()
				return
			}
			// Канал — тот же слот, что виджет ПОКАЗЫВАЕТ и шлёт бэку (правило pickAssetChannel, под тестом).
			const channel = channelOf(g2)
			const file = (u: string): UploadedFile => ({ file: dummyFile(), preview: u, url: u, sceneAsset: true })
			// Место считаем заново: пока шёл план, юзер мог догрузить своё фото.
			const roomNow = roomOf(channel, g2, own, limitsRef.current)
			let placed: ScenePlanItem[] = []
			let slotKept = false
			if (channel === 'uploaded') {
				// Единственный слот (i2i-база / первый кадр видео) — только первый вид.
				const slot = g2.uploadedImage
				const slotIsOurs = !!slot?.url && own.has(path(slot.url))
				const take = items.slice(0, 1)
				if (!take.length) {
					releaseSlot(g2, own, displaced)
				} else if (slot && !slotIsOurs && !o.replace) {
					// В слоте фото юзера, а сцену добавили попутно (упоминание, смена модели) — его фото остаётся.
					slotKept = true
				} else {
					if (slot && !slotIsOurs) displaced.current = slot.url ? { ...slot, preview: slot.url } : slot
					g2.setUploadedImage(file(take[0].url as string))
					placed = take
				}
			} else {
				releaseSlot(g2, own, displaced)
				placed = fitLayout(items, roomNow)
				const urls = placed.map((i) => i.url as string)
				if (urls.length) {
					if (channel === 'omni') g2.setOmniReferenceImages((p) => [...p, ...urls.map(file)])
					else g2.setReferenceImages((p) => [...p, ...urls.map(file)])
				}
			}
			const urls = placed.map((i) => i.url as string)
			const placedBy: Record<string, string[]> = {}
			for (const i of placed) (placedBy[i.asset_id] ||= []).push(i.url as string)
			const wordsOnly = assets.filter((a) => !placedBy[a.id]).map((a) => a.id)
			const ready = new Set(plan.assets.filter((a) => a.ready).map((a) => a.id))
			const warnings = sceneNoticeLines({
				fits: plan.fits,
				reason: plan.reason,
				warnings: plan.warnings,
				wordsOnly: assets.filter((a) => !placedBy[a.id] && ready.has(a.id)).map((a) => a.name),
				placed: urls.length > 0,
				slotKept,
			})
			setScene({
				host,
				assets,
				mode: plan.mode,
				warnings,
				// Барьер: сцена не подходит модели по существу (i2v «первый кадр»). Фото всё равно кладём
				// (красный бордер), но «Создать» блокируется — не даём сгенерировать заведомо не то.
				blocked: warnings.some((w) => w.blocking),
				channel,
				placedUrls: urls,
				placedBy,
				wordsOnly,
				room: roomNow,
				prefer,
				sheetUsed: placed.some((i) => i.role === 'sheet'),
			})
			// «Липкость»: сцена переживает смену карточки/стиля и F5. Забывается только явным снятием.
			setStickyScene(assets.map((a) => a.id), prefer, urls.map(path))
			if (!plan.fits && !o.quiet) showToast(SCENE_UNFIT_TEXTS[plan.reason ?? ''] ?? SCENE_UNFIT_DEFAULT, 'info')
		},
		[host, ownPaths],
	)

	/** Снять ассет (или всю сцену без id): его фото уходят из слотов, чипы — из промпта. keepPrompt — чип стёр сам
	 *  юзер правкой текста: текст уже новый, а у нас на руках прошлый рендер — не трогаем, иначе затёрли бы правку. */
	const removeAsset = useCallback(
		(id?: string, keepPrompt = false) => {
			if (!isPrimarySceneHost(host)) return
			const g = genRef.current
			const ids = id ? wanted().filter((x) => x !== id) : []
			if (!keepPrompt) {
				const stripped = stripAssetTokens(g.prompt, id ? [id] : undefined)
				if (stripped !== g.prompt) g.setPrompt(stripped)
			}
			void applyScene(ids, { quiet: true })
		},
		[applyScene, host, wanted],
	)

	/** План БЕЗ применения — пикер решает, показывать ли шаг «лист ↔ фото». */
	const probePlan = useCallback(async (ids: string[]): Promise<ScenePlan | null> => {
		const g = genRef.current
		const model = g.selectedModel
		if (!model || !ids.length) return null
		return planAssets({
			asset_ids: ids,
			adapter: model.adapter || model.provider,
			model: model.id,
			task: g.isVideo ? 'video' : 'photo',
		})
	}, [])

	// Снятие: «×» у строки статуса, чип убран из промпта, удалён свой ассет.
	useEffect(() => {
		const onRemove = (e: Event) => {
			const d = (e as CustomEvent<SceneRemoveRequest>).detail
			removeAsset(d?.id, !!d?.keepPrompt)
		}
		window.addEventListener(SCENE_REMOVE_EVENT, onRemove)
		return () => window.removeEventListener(SCENE_REMOVE_EVENT, onRemove)
	}, [removeAsset])

	// Сцена есть, но её клал не этот хост (перешли из виджета на страницу генератора, вышли из слота тренда, панель
	// пересоздалась) — раскладываем у себя заново. Сцены нет, а липкий набор есть (F5, сброс слотов под новую
	// карточку) — переприменяем. Оба пути без тостов; зацикливания нет: после неудачи зависимости не меняются.
	const scene = useScene()
	const modelId = gen.selectedModel?.id ?? null
	useEffect(() => {
		if (!userId || !modelId || !isPrimarySceneHost(host)) return
		if (scene) {
			if (scene.host !== host && !isSceneHostMounted(scene.host)) {
				// Слот героя тренда держит в сторе одного героя, а остальная сцена (локации, предметы) ждёт в липкости —
				// возвращаясь в обычный виджет, берём весь липкий набор, а не только героя.
				const sticky = scene.mode === 'PIPE' ? getStickyScene(userId) : null
				const ids = sticky?.ids.length ? sticky.ids : scene.assets.map((a) => a.id)
				void applyScene(ids, { prefer: scene.prefer ?? sticky?.prefer, quiet: true })
			}
			return
		}
		if (pending.current) return
		const sticky = getStickyScene(userId)
		if (sticky) void applyScene(sticky.ids.slice(0, MAX_SCENE_ASSETS), { prefer: sticky.prefer, quiet: true })
	}, [scene, userId, modelId, host, applyScene])

	// Смена модели ИЛИ под-режима Seedance при активной сцене → пере-план тем же набором: режим
	// «Кадры»/«Референсы» меняет ВИДИМЫЙ канал (слот кадра vs мульти-ряд).
	const prevModelId = useRef<string | null>(modelId)
	const prevSeedanceMode = useRef<string | undefined>(gen.settings.seedanceMode)
	useEffect(() => {
		const mode = gen.settings.seedanceMode
		const changed = prevModelId.current !== modelId || prevSeedanceMode.current !== mode
		prevModelId.current = modelId
		prevSeedanceMode.current = mode
		if (!changed || !modelId || !isPrimarySceneHost(host)) return
		const sel = getScene()
		if (sel && sel.host !== host) return // чужую сцену заберёт эффект выше
		const ids = wanted()
		if (ids.length) void applyScene(ids, { quiet: true })
	}, [modelId, gen.settings.seedanceMode, host, applyScene, wanted])

	// Упомянули в промпте ассет, которого нет в сцене (меню «@», восстановленный черновик) → добавить.
	useEffect(() => {
		if (!tools || !userId || !modelId || !isPrimarySceneHost(host)) return
		const sel = getScene()
		if (sel && sel.host !== host) return
		const cur = wanted()
		const missing = assetIdsInPrompt(gen.prompt).filter((id) => !cur.includes(id))
		if (!missing.length) return
		const next = [...cur, ...missing].slice(0, MAX_SCENE_ASSETS)
		if (next.length === cur.length) {
			// Сцена полна: лишние упоминания уйдут в промпт именем. Говорим один раз, а не на каждую букву.
			if (Date.now() - capToastAt.current > 60_000) {
				capToastAt.current = Date.now()
				showToast(`В сцене до ${MAX_SCENE_ASSETS} аватаров, локаций и предметов — остальные уйдут в промпт только именем`, 'info')
			}
			return
		}
		if (failed.current?.key === keyOf(next) && Date.now() - failed.current.at < RETRY_AFTER_MS) return
		void applyScene(next, { quiet: true })
	}, [gen.prompt, tools, userId, modelId, host, applyScene, wanted])

	// Страж честности: слоты изменились мимо нас.
	//  - фото ассета пропали после программного сброса (resetAll, новая карточка) — снимаем раскладку, липкость
	//    живёт (переприменимся);
	//  - юзер убрал фото ассета руками — ассет выходит из сцены целиком, вместе с чипами;
	//  - юзер положил в единственный слот своё фото поверх фото ассета — его фото важнее: ассет остаётся в сцене
	//    и идёт словами;
	//  - места стало больше (юзер убрал своё фото), а кто-то ждёт «словами» — раскладываем заново.
	// Сцена другого хоста — не наша забота: её след про чужие слоты.
	useEffect(() => {
		const sel = getScene()
		if (!sel || sel.host !== host) return
		const g = genRef.current
		const inChannels = [gen.uploadedImage?.url, ...gen.omniReferenceImages.map((i) => i.url), ...gen.referenceImages.map((i) => i.url)]
			.filter((u): u is string => !!u)
			.map(path)
		const lost = Object.entries(sel.placedBy)
			.filter(([, urls]) => urls.length && !urls.some((u) => inChannels.includes(path(u))))
			.map(([id]) => id)
		if (lost.length) {
			if (wasSceneChannelsResetRecently()) {
				displaced.current = null
				clearScene()
				return
			}
			if (sel.channel === 'uploaded' && gen.uploadedImage) {
				displaced.current = null
				void applyScene(wanted(), { quiet: true })
				return
			}
			const stripped = stripAssetTokens(g.prompt, lost)
			if (stripped !== g.prompt) g.setPrompt(stripped)
			void applyScene(wanted().filter((id) => !lost.includes(id)), { quiet: true })
			return
		}
		if (pending.current || !sel.wordsOnly.length || sel.room == null) return
		if (roomOf(channelOf(g), g, ownPaths(), limitsRef.current) > sel.room) void applyScene(wanted(), { quiet: true })
	}, [gen.uploadedImage, gen.referenceImages, gen.omniReferenceImages]) // eslint-disable-line react-hooks/exhaustive-deps

	return { applyScene, removeAsset, probePlan }
}
